import {
  GetActiveUniqueProfilesMetricsQueryVariables,
  GetActiveUniqueProfilesMetricsQuery,
  GetEmailCaptureRateQuery
} from '@/gql/graphql';
import { useQuery } from '@apollo/client';
import { GET_ACTIVE_UNIQUE_PROFILE_METRICS, GET_EMAIL_CAPTURE_RATE } from '../queries/metrics';
import { useProfileFilters, useRefetchLastDate } from '../store/store';
import { MetricCard } from './metric-card';
import { useEffect } from 'react';
import { Mail, Percent } from 'lucide-react';

export function ProfilesMetrics() {
  return (
    <div className="w-full px-2 py-6">
      <div className="flex flex-row items-start gap-6 overflow-x-auto">
        <ActiveUniqueEmailProfiles />
        <EmailCaptureRate />
      </div>
    </div>
  );
}

export function EmailCaptureRate() {
  const filters = useProfileFilters();
  const refetchLastDate = useRefetchLastDate();

  const queryFilters = (
    filter: typeof filters
  ): GetActiveUniqueProfilesMetricsQueryVariables | undefined => {
    if (filter.type !== 'valid') {
      return undefined;
    }

    const retVal: GetActiveUniqueProfilesMetricsQueryVariables = {
      filters: {
        startDate: filter.dateRange.startDate.toISOString(),
        endDate: filter.dateRange.endDate.toISOString()
      }
    };
    return retVal;
  };

  const { data, loading, refetch } = useQuery<GetEmailCaptureRateQuery>(GET_EMAIL_CAPTURE_RATE, {
    variables: {
      ...queryFilters(filters)
    },
    fetchPolicy: 'cache-and-network',
    skip: filters.type !== 'valid'
  });

  useEffect(() => {
    void refetch();
  }, [refetchLastDate, refetch]);

  return (
    <MetricCard
      title="Email Capture Rate"
      value={`${data?.getEmailCaptureRate.emailCaptureRate ?? 0}%`}
      description=""
      isLoading={loading}
      icon={
        <div className="rounded-full p-2 bg-primary text-primary-foreground">
          <Percent size={16} />
        </div>
      }
    />
  );
}

export function ActiveUniqueEmailProfiles() {
  const filters = useProfileFilters();
  const refetchLastDate = useRefetchLastDate();
  const queryFilters = (
    filter: typeof filters
  ): GetActiveUniqueProfilesMetricsQueryVariables | undefined => {
    if (filter.type !== 'valid') {
      return undefined;
    }

    const retVal: GetActiveUniqueProfilesMetricsQueryVariables = {
      filters: {
        startDate: filter.dateRange.startDate.toISOString(),
        endDate: filter.dateRange.endDate.toISOString()
      }
    };
    return retVal;
  };

  const { data, loading, refetch } = useQuery<GetActiveUniqueProfilesMetricsQuery>(
    GET_ACTIVE_UNIQUE_PROFILE_METRICS,
    {
      variables: {
        ...queryFilters(filters)
      },
      fetchPolicy: 'cache-and-network',
      skip: filters.type !== 'valid'
    }
  );

  useEffect(() => {
    void refetch();
  }, [refetchLastDate, refetch]);

  const totalProfiles = Intl.NumberFormat('en-US').format(
    data?.getActiveUniqueProfileMetrics.totalProfiles ?? 0
  );

  return (
    <MetricCard
      title="Active Unique Email Profiles"
      value={totalProfiles}
      description=""
      isLoading={loading}
      icon={
        <div className="rounded-full p-2 bg-primary text-primary-foreground">
          <Mail size={16} />
        </div>
      }
    />
  );
}
